import { APP_ID, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthNavigationService {
  readonly #router = inject(Router);
  readonly #app_id = inject(APP_ID);
  readonly #returnUrl = signal<string | null>(null);

  // Only router-serialized URLs may be stored, and only navigateByUrl may read
  // them back: that keeps the redirect inside the app. Never feed it a query
  // param or hand it to location.href.
  setReturnUrl(url: string | null) {
    this.#returnUrl.set(url);
  }

  getCurrentReturnUrl(): string | null {
    return this.#returnUrl();
  }

  navigateToLogin() {
    const currentUrl = this.#router.routerState.snapshot.url;
    // Several requests can answer 401 together: the later ones arrive once the
    // first has already landed on login, which is no page to return to.
    if (!currentUrl.startsWith('/auth/')) {
      this.setReturnUrl(currentUrl);
    }
    // Replacing the page keeps one copy of it in the history once the next
    // login restores it, so Back leaves it instead of revisiting it.
    this.#router.navigateByUrl('/auth/login', { replaceUrl: true });
  }

  navigateAfterLogout() {
    if (this.#app_id === 'checkout') return;
    if (this.#isControlHub()) {
      // Every hub page is guarded: passing through one on the way to login
      // would store it as the page to return to after the next login.
      this.setReturnUrl(null);
      this.#router.navigateByUrl('/auth/login');
      return;
    }
    this.#router.navigateByUrl('/events');
  }

  // The server ended the session; the user did not ask to leave. The hub
  // remembers the page so the next login returns to it.
  navigateAfterSessionExpired() {
    if (this.#isControlHub()) {
      this.navigateToLogin();
      return;
    }
    this.navigateAfterLogout();
  }

  navigateAfterLogin() {
    if (this.#isControlHub()) {
      this.#router.navigateByUrl('/');
    } else {
      return;
    }
  }

  #isControlHub() {
    return (
      this.#app_id === 'control-hub' || this.#app_id === 'control-hub-desktop'
    );
  }
}
